import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import { postData, API_ENDPOINTS } from "../../services/apiService";
import "./User-Sign-In.css";

function LogIn() {
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [mobileNumberError, setMobileNumberError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [mobileNumberTouched, setMobileNumberTouched] = useState(false);
  const [otpTouched, setOtpTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const navigate = useNavigate();

  const validateMobileNumber = (number) => {
    const regex = /^\d{10}$/;
    if (!number) return "Mobile number is required.";
    if (!regex.test(number)) return "Enter a valid 10-digit number.";
    return "";
  };

  const validateOtp = (code) => {
    const regex = /^\d{4,6}$/;
    if (!code) return "OTP is required.";
    if (!regex.test(code)) return "Enter a valid OTP (4–6 digits).";
    return "";
  };

  const handleMobileNumberChange = (e) => {
    const v = e.target.value.replace(/\D/g, "");
    setMobileNumber(v);
    if (mobileNumberTouched) setMobileNumberError(validateMobileNumber(v));
  };

  const handleOtpChange = (e) => {
    const v = e.target.value.replace(/\D/g, "");
    setOtp(v);
    if (otpTouched) setOtpError(validateOtp(v));
  };

  const handleGetOtp = async () => {
    const err = validateMobileNumber(mobileNumber);
    if (err) {
      setMobileNumberTouched(true);
      setMobileNumberError(err);
      return;
    }
    try {
      const response = await postData(API_ENDPOINTS.sendOtp, { mobile_num: Number(mobileNumber) });
      if (response.statusCode === 200) {
        setSnackbarMessage(`OTP sent! ${response.result?.otp ? `(${response.result.otp})` : ""}`);
        setSnackbarSeverity("success");
      } else {
        setSnackbarMessage(response.message || "Failed to send OTP");
        setSnackbarSeverity("error");
      }
    } catch {
      setSnackbarMessage("Error while sending OTP");
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMobileNumberTouched(true);
    setOtpTouched(true);
    const mErr = validateMobileNumber(mobileNumber);
    const oErr = validateOtp(otp);
    setMobileNumberError(mErr);
    setOtpError(oErr);
    if (mErr || oErr) return;

    setLoading(true);
    try {
      const data = await postData(API_ENDPOINTS.verifyOtpLogin, { mobile_num: Number(mobileNumber), otp });
      if (data.statusCode === 200) {
        localStorage.setItem("userData", JSON.stringify(data.result));
        localStorage.setItem("authToken", data.result.token);
        setSnackbarMessage(data.message || "Login successful");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => navigate("/app/dashboard"), 500);
      } else {
        setSnackbarMessage(data.message || "Login failed");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch {
      setSnackbarMessage("Error during login");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">

      {/* ── Left brand panel ── */}
      <div className="login-left-panel">
        <div className="brand-top">
          <div className="brand-logo-wrap">
            <img src="/images/sut-icon.png" alt="SUT" className="brand-logo" />
          </div>
          <span className="brand-badge">⚡ Admin Portal</span>
        </div>

        <div className="brand-middle">
          <h2 className="brand-heading">
            India's freight<br />ecosystem, unified.
          </h2>
          <p className="brand-sub">
            Manage trucks, drivers, bookings and payouts — all from one powerful dashboard.
          </p>

          <div className="brand-features">
            <div className="feature-pill">
              <div className="pill-icon">📦</div>
              Real-time shipment tracking
            </div>
            <div className="feature-pill">
              <div className="pill-icon">📊</div>
              Analytics & revenue insights
            </div>
            <div className="feature-pill">
              <div className="pill-icon">🚚</div>
              Fleet & driver management
            </div>
          </div>
        </div>

        <div className="brand-bottom">
          © {new Date().getFullYear()} Share Your Truck · All rights reserved
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="login-right-panel">
        <div className="login-card">
          <div className="login-card-header">
            <h1>Welcome back 👋</h1>
            <p>Sign in to your admin account to continue</p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Mobile number */}
            <div className="input-group">
              <label className="input-label">Mobile Number</label>
              <div className="input-wrapper">
                <span className="input-icon">📱</span>
                <input
                  type="tel"
                  className={`input-field has-action${mobileNumberError && mobileNumberTouched ? " input-error-state" : ""}`}
                  placeholder="Enter 10-digit number"
                  value={mobileNumber}
                  maxLength={10}
                  onChange={handleMobileNumberChange}
                  onBlur={() => {
                    setMobileNumberTouched(true);
                    setMobileNumberError(validateMobileNumber(mobileNumber));
                  }}
                />
                <button type="button" className="otp-action" onClick={handleGetOtp}>
                  Get OTP
                </button>
              </div>
              {mobileNumberError && mobileNumberTouched && (
                <p className="field-error">⚠ {mobileNumberError}</p>
              )}
            </div>

            {/* OTP */}
            <div className="input-group">
              <label className="input-label">One-Time Password</label>
              <div className="input-wrapper">
                <span className="input-icon">🔑</span>
                <input
                  type="tel"
                  className={`input-field${otpError && otpTouched ? " input-error-state" : ""}`}
                  placeholder="Enter OTP"
                  value={otp}
                  maxLength={6}
                  onChange={handleOtpChange}
                  onBlur={() => {
                    setOtpTouched(true);
                    setOtpError(validateOtp(otp));
                  }}
                />
              </div>
              {otpError && otpTouched && (
                <p className="field-error">⚠ {otpError}</p>
              )}
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default LogIn;
